import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment, type ReactNode } from 'react';

import { Button } from '../Button/Button';
import { cn } from '../lib/utils';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl';
type ModalRole = 'alertdialog' | 'dialog';

interface ModalAction {
  label: string;
  onClick?: () => void;
  color?: 'primary' | 'secondary' | 'destructive' | 'warning' | 'neutral';
  variant?: 'default' | 'outline' | 'ghost';
  isLoading?: boolean;
  disabled?: boolean;
}

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  primaryAction?: ModalAction;
  secondaryAction?: ModalAction;
  tertiaryAction?: ModalAction;
  icon?: ReactNode;
  size?: ModalSize;
  restrictCloseToButton?: boolean;
  overflowVisible?: boolean;
  blurredBackground?: boolean;
  className?: string;
  dataTestId?: string;
  role?: ModalRole;
  /**
   * Tailwind z-index for the dialog root. The default sits on the app's overlay
   * layer; raise it for a dialog that has to paint over a fixed overlay such as
   * the active call pill.
   */
  zIndexClassName?: string;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl'
};

function Modal({
  open,
  onClose,
  title,
  description,
  children,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  icon,
  size = 'md',
  restrictCloseToButton = false,
  overflowVisible = false,
  blurredBackground = false,
  className,
  dataTestId,
  role = 'dialog',
  zIndexClassName = 'z-30'
}: ModalProps) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        as="div"
        role={role}
        className={cn('relative', zIndexClassName)}
        data-testid={dataTestId}
        onClose={() => {
          if (!restrictCloseToButton) onClose();
        }}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div
            className={cn(
              'fixed inset-0 bg-gray-900/60 transition-opacity',
              blurredBackground && 'backdrop-blur-sm'
            )}
          />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95">
              <DialogPanel
                className={cn(
                  'relative w-full rounded-xl bg-white shadow-2xl',
                  'dark:bg-uniform-gray-900',
                  overflowVisible ? 'overflow-visible' : 'overflow-hidden',
                  'sm:my-8',
                  sizeClasses[size],
                  className
                )}>
                <div className="p-6">
                  {icon && (
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-uniform-gray-100 dark:bg-uniform-gray-800">
                      {icon}
                    </div>
                  )}

                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {title && (
                        <DialogTitle
                          as="h3"
                          className="text-lg font-semibold text-uniform-gray-900 dark:text-uniform-gray-100">
                          {title}
                        </DialogTitle>
                      )}
                      {description && (
                        <div className="mt-1 text-sm text-uniform-gray-600 dark:text-uniform-gray-400">
                          {description}
                        </div>
                      )}
                    </div>

                    {!restrictCloseToButton && (
                      <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="shrink-0 rounded p-1 text-uniform-gray-500 transition-colors hover:text-uniform-gray-700 dark:hover:text-uniform-gray-200 [outline:none] focus-visible:ring-2 focus-visible:ring-uniform-gray-400">
                        <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {children && <div className="mt-4">{children}</div>}

                  {(primaryAction || secondaryAction || tertiaryAction) && (
                    <div className="mt-6 flex justify-end gap-3">
                      {secondaryAction && (
                        <Button
                          variant={secondaryAction.variant ?? 'outline'}
                          color={secondaryAction.color ?? 'neutral'}
                          isLoading={secondaryAction.isLoading}
                          disabled={secondaryAction.disabled}
                          onClick={secondaryAction.onClick ?? onClose}>
                          {secondaryAction.label}
                        </Button>
                      )}
                      {tertiaryAction && (
                        <Button
                          variant={tertiaryAction.variant ?? 'outline'}
                          color={tertiaryAction.color ?? 'primary'}
                          isLoading={tertiaryAction.isLoading}
                          disabled={tertiaryAction.disabled}
                          onClick={tertiaryAction.onClick ?? onClose}>
                          {tertiaryAction.label}
                        </Button>
                      )}
                      {primaryAction && (
                        <Button
                          variant={primaryAction.variant ?? 'default'}
                          color={primaryAction.color ?? 'primary'}
                          isLoading={primaryAction.isLoading}
                          disabled={primaryAction.disabled}
                          onClick={primaryAction.onClick ?? onClose}>
                          {primaryAction.label}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export { Modal };
export type { ModalProps, ModalAction, ModalRole, ModalSize };
